// Frontend-only poster frames for video media.
//
// The media APIs do not return a thumbnail for uploaded video, and we are not
// allowed to add one. So the grid paints its own: load just the header of the
// existing signed URL (`preload = 'metadata'`), seek a fraction of a second in,
// and copy that single frame onto a canvas. Nothing is autoplayed and no file is
// fetched end to end - the browser range-requests only what the seek needs.
//
// Capture can legitimately fail (storage without permissive CORS taints the
// canvas, an exotic codec never decodes). A failure is remembered as `null` so
// the card falls back once to a muted <video> poster frame instead of retrying
// on every re-render.

const CACHE_LIMIT = 150;
const MAX_CONCURRENT = 2;
const SEEK_SECONDS = 0.5;
const LOAD_TIMEOUT_MS = 10000;
const THUMB_MAX_WIDTH = 480;

// key -> data url, or null for "tried, cannot be captured"
const cache = new Map();
const inFlight = new Map();

let activeCount = 0;
const waiting = [];

export const getCachedVideoThumbnail = (key) => cache.get(key);

export const hasCachedVideoThumbnail = (key) => cache.has(key);

// Exposed for tests; the cache is deliberately module level so a thumbnail
// survives the card unmounting as the user scrolls the grid.
export const clearVideoThumbnailCache = () => {
  cache.clear();
  inFlight.clear();
  waiting.length = 0;
  activeCount = 0;
};

const remember = (key, value) => {
  if (cache.size >= CACHE_LIMIT) {
    const oldest = cache.keys().next().value;
    cache.delete(oldest);
  }
  cache.set(key, value);
};

export const canCaptureVideoFrames = () => {
  if (typeof document === 'undefined') return false;
  try {
    const canvas = document.createElement('canvas');
    return typeof canvas.getContext === 'function' && !!canvas.getContext('2d');
  } catch (e) {
    return false;
  }
};

const captureFrame = (url) =>
  new Promise((resolve) => {
    let settled = false;
    const video = document.createElement('video');

    const finish = (result) => {
      if (settled) return;
      settled = true;
      clearTimeout(timer);
      try {
        video.removeAttribute('src');
        video.load();
      } catch (e) {
        /* releasing the element is best effort */
      }
      resolve(result);
    };

    const timer = setTimeout(() => finish(null), LOAD_TIMEOUT_MS);

    video.crossOrigin = 'anonymous';
    video.preload = 'metadata';
    video.muted = true;
    video.playsInline = true;

    video.addEventListener('loadedmetadata', () => {
      const half = (video.duration || 1) / 2;
      const target = Math.min(SEEK_SECONDS, Number.isFinite(half) ? half : SEEK_SECONDS);
      try {
        video.currentTime = Number.isFinite(target) && target > 0 ? target : 0;
      } catch (e) {
        finish(null);
      }
    });

    video.addEventListener('seeked', () => {
      try {
        const width = video.videoWidth;
        const height = video.videoHeight;
        if (!width || !height) return finish(null);

        const scale = Math.min(1, THUMB_MAX_WIDTH / width);
        const canvas = document.createElement('canvas');
        canvas.width = Math.round(width * scale);
        canvas.height = Math.round(height * scale);

        const context = canvas.getContext('2d');
        if (!context) return finish(null);

        context.drawImage(video, 0, 0, canvas.width, canvas.height);
        // Throws a SecurityError when the storage origin did not allow the read.
        finish(canvas.toDataURL('image/jpeg', 0.7));
      } catch (e) {
        finish(null);
      }
    });

    video.addEventListener('error', () => finish(null));

    video.src = url;
    try {
      video.load();
    } catch (e) {
      finish(null);
    }
  });

const runNext = () => {
  const task = waiting.shift();
  if (!task) return;
  activeCount += 1;
  task().finally(() => {
    activeCount -= 1;
    pump();
  });
};

const pump = () => {
  while (activeCount < MAX_CONCURRENT && waiting.length > 0) runNext();
};

/**
 * Resolves to a data URL for a frame of `url`, or null when no frame can be
 * captured. Decoding is capped at MAX_CONCURRENT so a grid of fifty videos does
 * not open fifty connections at once.
 */
export const requestVideoThumbnail = (url, key = url) => {
  if (!url) return Promise.resolve(null);
  if (cache.has(key)) return Promise.resolve(cache.get(key));
  if (inFlight.has(key)) return inFlight.get(key);
  if (!canCaptureVideoFrames()) {
    remember(key, null);
    return Promise.resolve(null);
  }

  const promise = new Promise((resolve) => {
    waiting.push(() =>
      captureFrame(url).then((result) => {
        remember(key, result);
        inFlight.delete(key);
        resolve(result);
      })
    );
    pump();
  });

  inFlight.set(key, promise);
  return promise;
};
